import { useEffect, useRef, type RefObject } from 'react';
import { useDragLayer, type XYCoord } from 'react-dnd';
import { PHASE_DRAG_TYPE } from '../../../lib/dndTypes';
import { autoScrollDelta, EDGE_ZONE_PX, MAX_AUTOSCROLL_PX } from '../../../lib/autoScroll';

/**
 * #217 v2 — edge auto-pan. While a phase drag is active, scroll the board
 * horizontally when the cursor nears an edge so off-screen dates come into view
 * (the #218 sentinel observer then slides the mounted week-window). Renders null.
 * MUST be mounted inside the board's <DndProvider> (it calls useDragLayer).
 */
export function DragAutoScroller({ scrollRef }: { scrollRef: RefObject<HTMLDivElement | null> }) {
  const { isPhaseDrag, cursor } = useDragLayer((m) => ({
    isPhaseDrag: m.isDragging() && m.getItemType() === PHASE_DRAG_TYPE,
    cursor: m.getClientOffset(),
  }));

  // Keep the latest cursor in a ref so the RAF loop reads it without subscribing.
  // An UNKEYED effect refreshes it after every render (useDragLayer re-renders on
  // each pointer move during a drag); it runs before the next animation frame, so
  // the loop never reads a stale cursor — and we avoid writing a ref during render.
  const cursorRef = useRef<XYCoord | null>(null);
  useEffect(() => {
    cursorRef.current = cursor;
  });

  useEffect(() => {
    if (!isPhaseDrag) return;
    let raf = 0;
    const loop = () => {
      const el = scrollRef.current;
      const c = cursorRef.current;
      if (!el || !c) {
        raf = requestAnimationFrame(loop);
        return;
      }
      const delta = autoScrollDelta(
        c.x,
        el.getBoundingClientRect(),
        EDGE_ZONE_PX,
        MAX_AUTOSCROLL_PX,
      );
      if (delta !== 0) el.scrollLeft += delta;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [isPhaseDrag, scrollRef]);

  return null;
}
